import React, { useState, useEffect } from 'react';
import { useProvider } from '../../contexts/ProviderContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';
import {
  UserGroupIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ProviderLayout from './ProviderLayout';

interface MerchantEntry {
  merchant_id: string;
  merchant_name: string;
  shop_domain: string;
  stock_provider: boolean;
  orders_provider: boolean;
  connection_request_id: string | null;
  connection_status: 'pending' | 'approved' | 'rejected' | 'connected';
  requested_at: string | null;
  reviewed_at: string | null;
  addresses: Record<string, unknown> | null;
  capabilities_requested: Record<string, unknown> | null;
  merchant_credentials: Record<string, unknown> | null;
  auto_order_config: Record<string, unknown> | null;
  rejection_reason: string | null;
  notes: string | null;
  credential_type: 'api_key' | 'login_password' | 'none';
}

type FilterTab = 'all' | 'pending' | 'approved' | 'rejected';

type ReviewModal =
  | null
  | { type: 'approve'; entry: MerchantEntry }
  | { type: 'reject'; entry: MerchantEntry };

const ProviderMerchants: React.FC = () => {
  const { currentProviderId } = useProvider();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<MerchantEntry[]>([]);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reviewModal, setReviewModal] = useState<ReviewModal>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (currentProviderId) {
      loadMerchants();
    }
  }, [currentProviderId]);

  const loadMerchants = async () => {
    if (!currentProviderId) return;

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .rpc('get_provider_merchants', { p_provider_id: currentProviderId });

      if (fetchError) throw fetchError;
      setEntries((data as MerchantEntry[]) || []);
    } catch (err) {
      console.error('Error loading merchants:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (action: 'approve' | 'reject') => {
    if (!reviewModal) return;
    const credType = reviewModal.entry.credential_type;

    if (action === 'approve') {
      if (credType === 'api_key' && !apiKeyInput.trim()) {
        setReviewError('API key is required to approve this connection');
        return;
      }
      if (credType === 'login_password' && (!usernameInput.trim() || !passwordInput.trim())) {
        setReviewError('Username and password are required to approve this connection');
        return;
      }
    }

    setReviewLoading(true);
    setReviewError(null);

    try {
      const payload: Record<string, unknown> = {
        request_id: reviewModal.entry.connection_request_id,
        action,
      };

      if (action === 'approve') {
        if (credType === 'api_key') {
          payload.api_key = apiKeyInput.trim();
        } else if (credType === 'login_password') {
          payload.username = usernameInput.trim();
          payload.password = passwordInput.trim();
        }
      }

      if (action === 'reject' && rejectionReason.trim()) {
        payload.rejection_reason = rejectionReason.trim();
      }

      const { data, error: invokeError } = await supabase.functions.invoke(
        'review-connection-request',
        { body: payload },
      );

      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);

      const merchantName = getMerchantDisplayName(reviewModal.entry);
      toast.success(
        action === 'approve'
          ? `Approved connection for ${merchantName}`
          : `Rejected connection for ${merchantName}`,
      );
      closeReviewModal();
      await loadMerchants();
    } catch (err) {
      console.error(`Error ${action}ing request:`, err);
      setReviewError(err instanceof Error ? err.message : `Failed to ${action} request`);
    } finally {
      setReviewLoading(false);
    }
  };

  const openApproveModal = (entry: MerchantEntry) => {
    setReviewModal({ type: 'approve', entry });
    setApiKeyInput('');
    setUsernameInput('');
    setPasswordInput('');
    setReviewError(null);
  };

  const openRejectModal = (entry: MerchantEntry) => {
    setReviewModal({ type: 'reject', entry });
    setRejectionReason('');
    setReviewError(null);
  };

  const closeReviewModal = () => {
    setReviewModal(null);
    setApiKeyInput('');
    setUsernameInput('');
    setPasswordInput('');
    setRejectionReason('');
    setReviewError(null);
  };

  const isApprovedOrConnected = (status: string) =>
    status === 'approved' || status === 'connected';

  const filteredEntries = activeTab === 'all'
    ? entries
    : activeTab === 'approved'
      ? entries.filter((e) => isApprovedOrConnected(e.connection_status))
      : entries.filter((e) => e.connection_status === activeTab);

  const counts = {
    all: entries.length,
    pending: entries.filter((e) => e.connection_status === 'pending').length,
    approved: entries.filter((e) => isApprovedOrConnected(e.connection_status)).length,
    rejected: entries.filter((e) => e.connection_status === 'rejected').length,
  };

  const getMerchantDisplayName = (entry: MerchantEntry): string => {
    if (entry.merchant_name) return entry.merchant_name;
    const addr = entry.addresses as any;
    if (addr?.store_shipping_address?.name) return addr.store_shipping_address.name;
    if (addr?.store_billing_address?.company_name) return addr.store_billing_address.company_name;
    return entry.merchant_id.slice(0, 8) + '...';
  };

  const getMerchantDomain = (entry: MerchantEntry): string | null => {
    if (entry.shop_domain) return entry.shop_domain;
    const creds = entry.merchant_credentials as any;
    if (creds?.shop_domain) return creds.shop_domain;
    return null;
  };

  const getCapabilities = (entry: MerchantEntry): string[] => {
    const result: string[] = [];
    if (entry.stock_provider) result.push('Stock');
    if (entry.orders_provider) result.push('Orders');
    if (result.length === 0) {
      const caps = entry.capabilities_requested as any;
      if (caps?.stock_provider) result.push('Stock');
      if (caps?.orders_provider) result.push('Orders');
    }
    if (result.length === 0) result.push('Not specified');
    return result;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { bg: string; text: string; icon: React.ReactNode; label: string }> = {
      pending: {
        bg: 'bg-yellow-500/10 border-yellow-500/20',
        text: 'text-yellow-400',
        icon: <ClockIcon className="w-3.5 h-3.5" />,
        label: 'PENDING',
      },
      approved: {
        bg: 'bg-green-500/10 border-green-500/20',
        text: 'text-green-400',
        icon: <CheckCircleIcon className="w-3.5 h-3.5" />,
        label: 'APPROVED',
      },
      connected: {
        bg: 'bg-green-500/10 border-green-500/20',
        text: 'text-green-400',
        icon: <CheckCircleIcon className="w-3.5 h-3.5" />,
        label: 'CONNECTED',
      },
      rejected: {
        bg: 'bg-red-500/10 border-red-500/20',
        text: 'text-red-400',
        icon: <XCircleIcon className="w-3.5 h-3.5" />,
        label: 'REJECTED',
      },
    };
    const c = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono font-semibold rounded-full border ${c.bg} ${c.text}`}>
        {c.icon}
        {c.label}
      </span>
    );
  };

  const renderDetailSection = (title: string, data: Record<string, unknown> | null | undefined) => {
    if (!data || Object.keys(data).length === 0) return null;
    return (
      <div>
        <h4 className="text-sm font-mono font-semibold text-white/70 uppercase mb-2">{title}</h4>
        <div className="bg-white/[0.03] rounded p-3 text-sm font-mono text-white/80 overflow-x-auto">
          <pre className="whitespace-pre-wrap">{JSON.stringify(data, null, 2)}</pre>
        </div>
      </div>
    );
  };

  const renderExpandedRow = (entry: MerchantEntry) => {
    return (
      <tr>
        <td colSpan={6} className="px-6 py-4 bg-white/[0.02] border-b border-white/[0.06]">
          <div className="space-y-4">
            {entry.notes && (
              <div>
                <h4 className="text-sm font-mono font-semibold text-white/70 uppercase mb-2">Notes</h4>
                <p className="text-sm text-white/80">{entry.notes}</p>
              </div>
            )}
            {renderDetailSection('Merchant Credentials', entry.merchant_credentials)}
            {renderDetailSection('Addresses', entry.addresses)}
            {renderDetailSection('Capabilities Requested', entry.capabilities_requested)}
            {renderDetailSection('Auto-Order Config', entry.auto_order_config)}
            {entry.rejection_reason && (
              <div>
                <h4 className="text-sm font-mono font-semibold text-white/70 uppercase mb-2">Rejection Reason</h4>
                <p className="text-sm text-red-400">{entry.rejection_reason}</p>
              </div>
            )}
            {entry.reviewed_at && (
              <p className="text-xs text-white/40 font-mono">
                Reviewed: {formatDate(entry.reviewed_at)}
              </p>
            )}
            {entry.connection_status === 'pending' && (
              <div className="flex gap-3 pt-2 border-t border-white/[0.06]">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openApproveModal(entry);
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-mono font-semibold text-white bg-green-600 rounded hover:bg-green-700 transition-colors"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  Approve
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openRejectModal(entry);
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-mono font-semibold text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
                >
                  <XCircleIcon className="w-4 h-4" />
                  Reject
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const isApproveDisabled = () => {
    if (reviewLoading) return true;
    if (!reviewModal || reviewModal.type !== 'approve') return true;
    const credType = reviewModal.entry.credential_type;
    if (credType === 'api_key') return !apiKeyInput.trim();
    if (credType === 'login_password') return !usernameInput.trim() || !passwordInput.trim();
    return false; // 'none' — always enabled
  };

  const renderCredentialFields = (credType: string) => {
    if (credType === 'api_key') {
      return (
        <div>
          <label className="block text-sm font-medium text-white/70 mb-2 font-mono">
            API Key <span className="text-red-400">*</span>
          </label>
          <input
            type="password"
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            className="w-full px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 font-mono text-sm text-white placeholder-white/30"
            placeholder="Enter API key"
            disabled={reviewLoading}
          />
          <p className="text-xs text-white/40 mt-1 font-mono">
            Required. Stored securely in vault and used for inventory sync.
          </p>
        </div>
      );
    }

    if (credType === 'login_password') {
      return (
        <>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2 font-mono">
              Username <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              className="w-full px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 font-mono text-sm text-white placeholder-white/30"
              placeholder="Enter username"
              disabled={reviewLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2 font-mono">
              Password <span className="text-red-400">*</span>
            </label>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              className="w-full px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 font-mono text-sm text-white placeholder-white/30"
              placeholder="Enter password"
              disabled={reviewLoading}
            />
          </div>
          <p className="text-xs text-white/40 font-mono">
            Required. Credentials are stored securely in vault.
          </p>
        </>
      );
    }

    // credType === 'none'
    return (
      <p className="text-sm text-white/50 font-mono">
        No credentials required. Stock will sync automatically after approval.
      </p>
    );
  };

  const renderApproveModal = () => {
    if (!reviewModal || reviewModal.type !== 'approve') return null;
    const entry = reviewModal.entry;
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-[#1a1a1a] border border-white/[0.08] rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
            <h2 className="text-lg font-bold text-white font-mono">Approve Connection</h2>
            <button
              onClick={closeReviewModal}
              className="text-white/40 hover:text-white/70"
              disabled={reviewLoading}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-green-500/10 border border-green-500/20 rounded p-3">
              <p className="text-sm text-green-400 font-mono">
                Approve <strong>{getMerchantDisplayName(entry)}</strong> to connect as a merchant.
              </p>
              {getMerchantDomain(entry) && (
                <p className="text-xs text-green-500/70 mt-1 font-mono">{getMerchantDomain(entry)}</p>
              )}
            </div>
            <div className="text-sm text-white/60 font-mono">
              <p className="font-semibold mb-1">Requested capabilities:</p>
              <div className="flex gap-1.5">
                {getCapabilities(entry).map((cap) => (
                  <span
                    key={cap}
                    className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-500/10 text-blue-400 rounded border border-blue-500/20"
                  >
                    {cap}
                  </span>
                ))}
              </div>
            </div>
            {renderCredentialFields(entry.credential_type)}
            {reviewError && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm">
                {reviewError}
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/[0.08] bg-white/[0.02] rounded-b-lg">
            <button
              onClick={closeReviewModal}
              disabled={reviewLoading}
              className="px-4 py-2 text-sm font-mono text-white/70 bg-white/[0.05] border border-white/[0.1] rounded hover:bg-white/[0.08] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleReview('approve')}
              disabled={isApproveDisabled()}
              className="px-4 py-2 text-sm font-mono font-semibold text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
            >
              {reviewLoading ? 'Approving...' : 'Confirm Approval'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRejectModal = () => {
    if (!reviewModal || reviewModal.type !== 'reject') return null;
    const entry = reviewModal.entry;
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-[#1a1a1a] border border-white/[0.08] rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
            <h2 className="text-lg font-bold text-white font-mono">Reject Connection</h2>
            <button
              onClick={closeReviewModal}
              className="text-white/40 hover:text-white/70"
              disabled={reviewLoading}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-red-500/10 border border-red-500/20 rounded p-3">
              <p className="text-sm text-red-400 font-mono">
                Reject the connection request from <strong>{getMerchantDisplayName(entry)}</strong>.
              </p>
              {getMerchantDomain(entry) && (
                <p className="text-xs text-red-500/70 mt-1 font-mono">{getMerchantDomain(entry)}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2 font-mono">
                Reason for rejection (optional)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3 py-2 bg-white/[0.05] border border-white/[0.1] rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-sm text-white placeholder-white/30"
                placeholder="e.g., Incomplete credentials, not accepting new merchants..."
                rows={3}
                disabled={reviewLoading}
              />
            </div>
            {reviewError && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm">
                {reviewError}
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/[0.08] bg-white/[0.02] rounded-b-lg">
            <button
              onClick={closeReviewModal}
              disabled={reviewLoading}
              className="px-4 py-2 text-sm font-mono text-white/70 bg-white/[0.05] border border-white/[0.1] rounded hover:bg-white/[0.08] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleReview('reject')}
              disabled={reviewLoading}
              className="px-4 py-2 text-sm font-mono font-semibold text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
            >
              {reviewLoading ? 'Rejecting...' : 'Confirm Rejection'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
  ];

  const badges = counts.pending > 0
    ? { '/provider/merchants': <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full font-bold">{counts.pending}</span> }
    : undefined;

  return (
    <ProviderLayout badges={badges}>
      <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white ">MERCHANTS</h1>
              <p className="text-white/50 mt-2">
                Review connection requests from merchants
              </p>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 mb-6">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 text-sm font-mono font-semibold rounded transition-colors ${
                    activeTab === tab.key
                      ? 'bg-white text-black'
                      : 'bg-white/[0.05] text-white/70 border border-white/[0.08] hover:bg-white/[0.08]'
                  }`}
                >
                  {tab.label}
                  <span className="ml-2 text-xs opacity-75">({counts[tab.key]})</span>
                </button>
              ))}
            </div>

            {/* Requests Table */}
            <div className="bg-white/[0.02] rounded-lg border border-white/[0.08]">
              {loading ? (
                <div className="p-12 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  <p className="mt-4 text-white/50 font-mono">Loading requests...</p>
                </div>
              ) : filteredEntries.length === 0 ? (
                <div className="p-12 text-center">
                  <UserGroupIcon className="w-16 h-16 mx-auto text-white/20 mb-4" />
                  <p className="text-white/50 font-mono">
                    {activeTab === 'all'
                      ? 'No connection requests yet'
                      : `No ${activeTab} requests`}
                  </p>
                  <p className="text-white/30 text-sm mt-2 font-mono">
                    Merchants will appear here when they request to connect
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-white/[0.08]">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-mono font-semibold text-white/50 uppercase tracking-wider">
                          Merchant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-mono font-semibold text-white/50 uppercase tracking-wider">
                          Capabilities
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-mono font-semibold text-white/50 uppercase tracking-wider">
                          Submitted
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-mono font-semibold text-white/50 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-mono font-semibold text-white/50 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.06]">
                      {filteredEntries.map((entry) => {
                        const rowKey = entry.connection_request_id || entry.merchant_id;
                        return (
                          <React.Fragment key={rowKey}>
                            <tr
                              className="hover:bg-white/[0.03] transition-colors cursor-pointer"
                              onClick={() => setExpandedId(expandedId === rowKey ? null : rowKey)}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <p className="text-sm font-medium text-white">
                                    {getMerchantDisplayName(entry)}
                                  </p>
                                  {getMerchantDomain(entry) && (
                                    <p className="text-xs text-white/40">{getMerchantDomain(entry)}</p>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex gap-1.5">
                                  {getCapabilities(entry).map((cap) => (
                                    <span
                                      key={cap}
                                      className="inline-flex items-center px-2 py-0.5 text-xs font-mono bg-blue-500/10 text-blue-400 rounded border border-blue-500/20"
                                    >
                                      {cap}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white/50 font-mono">
                                {entry.requested_at ? formatDate(entry.requested_at) : '—'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {getStatusBadge(entry.connection_status)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                  {entry.connection_status === 'pending' ? (
                                    <>
                                      <button
                                        onClick={() => openApproveModal(entry)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-mono font-semibold text-green-400 bg-green-500/10 border border-green-500/20 rounded hover:bg-green-500/20 transition-colors"
                                      >
                                        <CheckCircleIcon className="w-3.5 h-3.5" />
                                        Approve
                                      </button>
                                      <button
                                        onClick={() => openRejectModal(entry)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-mono font-semibold text-red-400 bg-red-500/10 border border-red-500/20 rounded hover:bg-red-500/20 transition-colors"
                                      >
                                        <XCircleIcon className="w-3.5 h-3.5" />
                                        Reject
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      onClick={() => setExpandedId(expandedId === rowKey ? null : rowKey)}
                                      className="inline-flex items-center gap-1 text-orange-400 hover:text-orange-300 font-mono font-semibold"
                                    >
                                      {expandedId === rowKey ? (
                                        <>
                                          Hide <ChevronUpIcon className="w-4 h-4" />
                                        </>
                                      ) : (
                                        <>
                                          View <ChevronDownIcon className="w-4 h-4" />
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {expandedId === rowKey && renderExpandedRow(entry)}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

      {/* Modals */}
      {renderApproveModal()}
      {renderRejectModal()}
    </ProviderLayout>
  );
};

export default ProviderMerchants;
